import type { ModeloMensagem } from "@/lib/modelos";
import type { AreaKey, Papel } from "@/lib/permissoes";
import type { ProvedorId } from "@/lib/ports/whatsapp";
import type { Resultado, Teste } from "@/lib/telas/casos-config";
import type { Cadastro } from "@/lib/telas/empresa";
import type { Tenant } from "@/lib/tenants/types";
import { api } from "./api";
import { queryClient } from "./consultas";

/**
 * O que muda a clínica — cadastro, conexão, modelos, equipe, sistema —, por
 * chamada à API, com a resposta que a tela sabe mostrar.
 *
 * Mexer na clínica — cadastro, conexão, integrações, equipe — pode mudar o que
 * QUALQUER tela mostra (o nome no dock, os modelos da conversa, quem assumiu).
 * Por isso, depois de cada uma, tudo o que está guardado vira velho: o que está
 * na tela é buscado de novo por trás, o resto quando for aberto.
 */
async function configurar<T extends { ok: boolean }>(caminho: string, metodo: string, corpo?: unknown): Promise<T | Resultado> {
  try {
    return await api<T>(caminho, { method: metodo, body: corpo === undefined ? undefined : JSON.stringify(corpo) });
  } catch (e) {
    return { ok: false, erro: e instanceof Error ? e.message : String(e) };
  } finally {
    void queryClient.invalidateQueries();
  }
}

export async function salvarEmpresaAction(dados: { cadastro: Cadastro; agente: Tenant["agente"]; constituicao: string }): Promise<Resultado> {
  return configurar("/empresa", "PUT", dados);
}

export async function salvarConexaoAction(
  provedor: ProvedorId,
  campos: Record<string, string>,
  apenasTestar: boolean,
): Promise<Resultado & { teste?: Teste }> {
  return configurar("/conexao", "POST", { provedor, campos, apenasTestar });
}

export async function removerConexaoAction(): Promise<Resultado> {
  return configurar("/conexao", "DELETE");
}

export async function salvarIntegracoesAction(dados: {
  agenda: "memoria" | "google";
  ficha: "memoria" | "google";
  calendarId: string;
  spreadsheetId: string;
  aba: string;
}): Promise<Resultado> {
  return configurar("/conexao/integracoes", "PUT", dados);
}

export async function criarModeloAction(modelo: ModeloMensagem): Promise<Resultado> {
  return configurar("/modelos", "POST", modelo);
}
export async function atualizarModeloAction(nomeAtual: string, modelo: ModeloMensagem): Promise<Resultado> {
  return configurar(`/modelos/${encodeURIComponent(nomeAtual)}`, "PUT", modelo);
}
export async function removerModeloAction(nome: string): Promise<Resultado> {
  return configurar(`/modelos/${encodeURIComponent(nome)}`, "DELETE");
}

export async function convidarAction(email: string, papel: Papel): Promise<Resultado> {
  return configurar("/equipe/convites", "POST", { email, papel });
}
export async function cancelarConviteAction(conviteId: string): Promise<Resultado> {
  return configurar(`/equipe/convites/${encodeURIComponent(conviteId)}`, "DELETE");
}
export async function alterarPapelAction(userId: string, papel: Papel): Promise<Resultado> {
  return configurar(`/equipe/membros/${encodeURIComponent(userId)}/papel`, "PUT", { papel });
}
export async function removerMembroAction(userId: string): Promise<Resultado> {
  return configurar(`/equipe/membros/${encodeURIComponent(userId)}`, "DELETE");
}
export async function definirAcessoAction(userId: string, areas: AreaKey[] | null): Promise<Resultado> {
  return configurar(`/equipe/membros/${encodeURIComponent(userId)}/acesso`, "PUT", { areas });
}

export async function arquivarClinicaAction(slug: string): Promise<Resultado> {
  return configurar(`/sistema/clinicas/${slug}/arquivar`, "POST");
}
export async function restaurarClinicaAction(slug: string): Promise<Resultado> {
  return configurar(`/sistema/clinicas/${slug}/restaurar`, "POST");
}
export async function excluirClinicaAction(slug: string): Promise<Resultado> {
  return configurar(`/sistema/clinicas/${slug}`, "DELETE");
}
export async function entrarComoAdminAction(clinicaId: string): Promise<Resultado> {
  return configurar(`/sistema/organizacoes/${clinicaId}/entrar`, "POST");
}
